import { useCallback, useState } from 'react'
import { ISortOrderEnum } from 'types/graphqlTypes'
import { IPriceRequestDetailsFragment } from '../../graphql/PriceRequestDetailsFragment'
import { IPriceRequestsQueryVariables, usePriceRequestsQuery } from '../../graphql/PriceRequests'
import { TQuoteFilters } from '../types'

interface IUseHistoricalQuotesProps {
  sortBy?: string
  sortDirection?: ISortOrderEnum
  quotesPerPage?: number
  quotesFilters: TQuoteFilters
  setFocusedQuoteId: React.Dispatch<React.SetStateAction<string | null>>
}

export const useHistoricalQuotes = ({
  quotesFilters,
  quotesPerPage = 50,
  setFocusedQuoteId,
  sortBy,
  sortDirection,
}: IUseHistoricalQuotesProps) => {
  const [currentPagination, setCurrentPagination] = useState<
    Pick<IPriceRequestsQueryVariables, 'after' | 'before' | 'first' | 'last'>
  >({ first: quotesPerPage })
  const [currentPage, setCurrentPage] = useState<number>(1)

  const getQueryVariables = useCallback((): IPriceRequestsQueryVariables => {
    return {
      sortBy,
      sortDirection,
      isTestQuote: quotesFilters.isTestQuote,
      externalId: quotesFilters.externalId,
      priceRequestId: quotesFilters.priceRequestId,
      createdBy: quotesFilters.createdBy,
      equipmentParentId: quotesFilters.equipmentParentId,
      isMultiStop: quotesFilters.isMultiStop,
      dropType: quotesFilters.dropType,
      createdStartDate: quotesFilters.createdStartDate,
      createdEndDate: quotesFilters.createdEndDate,
      shipperApplicationId: quotesFilters.shipperApplicationId,
      pickupCity: quotesFilters.pickupCity,
      pickupStateCode: quotesFilters.pickupStateCode,
      deliveryCity: quotesFilters.deliveryCity,
      deliveryStateCode: quotesFilters.deliveryStateCode,
      pickupRange: quotesFilters.pickupRange,
      deliveryRange: quotesFilters.deliveryRange,
      after: currentPagination.after,
      before: currentPagination.before,
      first: currentPagination.first,
      last: currentPagination.last,
    }
  }, [sortBy, sortDirection, quotesFilters, quotesPerPage, currentPagination])

  const { data, fetchMore, loading, refetch: refetchPriceRequests } = usePriceRequestsQuery({
    variables: getQueryVariables(),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  })

  const { priceRequests } = data || {}
  const { edges, pageInfo, totalCount = 0 } = priceRequests || {}
  const { endCursor, hasNextPage: hasMorePagesToFetch = false, startCursor } = pageInfo ?? {}

  const paginatedPriceRequests: IPriceRequestDetailsFragment[] =
    edges?.map(edge => edge?.node as IPriceRequestDetailsFragment) || []

  const handleFetchFirstPage = useCallback(() => {
    refetchPriceRequests({
      first: quotesPerPage,
      sortBy,
      sortDirection,
      createdBy: quotesFilters.createdBy,
      equipmentParentId: quotesFilters.equipmentParentId,
      isMultiStop: quotesFilters.isMultiStop,
      dropType: quotesFilters.dropType,
      createdStartDate: quotesFilters.createdStartDate,
      createdEndDate: quotesFilters.createdEndDate,
      isTestQuote: quotesFilters.isTestQuote,
      externalId: quotesFilters.externalId,
      shipperApplicationId: quotesFilters.shipperApplicationId,
      priceRequestId: quotesFilters.priceRequestId,
      pickupCity: quotesFilters.pickupCity,
      pickupStateCode: quotesFilters.pickupStateCode,
      deliveryCity: quotesFilters.deliveryCity,
      deliveryStateCode: quotesFilters.deliveryStateCode,
      pickupRange: quotesFilters.pickupRange,
      deliveryRange: quotesFilters.deliveryRange,
    })
  }, [refetchPriceRequests])

  const handlePageChange = useCallback(
    async pageNumber => {
      if (pageNumber > currentPage && hasMorePagesToFetch) {
        setCurrentPagination({ after: endCursor, first: quotesPerPage })
        await fetchMore({
          variables: {
            ...getQueryVariables(),
            after: endCursor,
            first: quotesPerPage,
          },
        })
      } else {
        setCurrentPagination({ before: startCursor, last: quotesPerPage })
      }
      setFocusedQuoteId(null)
      setCurrentPage(pageNumber)
    },
    [endCursor, startCursor]
  )

  const totalPages = Math.ceil(totalCount / quotesPerPage)

  return {
    paginatedPriceRequests,
    currentPage,
    loading,
    totalCount: totalCount || 0,
    totalPages,
    handlePageChange,
    handleFetchFirstPage,
  }
}
